/** `charge.<letter>.<number>.description`: the identity of a charge line. */
const CHARGE_DESCRIPTION_RE = /^(charge\.[A-Z]\.[0-9]+)\.description$/;
/** "<text> to <Payee>", the same pattern the table reader uses for payees. */
const PAYEE_TAIL_RE = /^(.*\S)\s+to\s+([A-Z][^()]*\S)$/;

export type ExtractedLine = { key: string; value: string; src: LineSourceLike | null };
type LineSourceLike = { file: string; page: number; line: string };

/**
 * A charge description names its payee ("Appraisal Fee to Lone Star National Bank").
 * The payee belongs in its own `payee` field, so the description keeps only the charge text.
 * An extracted payee for the same charge is kept as it is; otherwise the split-off name is added.
 */
export const splitChargePayees = <T extends ExtractedLine>(lines: readonly T[]): T[] => {
  const payeeKeys = new Set(lines.map((line) => line.key));
  const extra: T[] = [];
  const cleaned = lines.map((line) => {
    const identity = CHARGE_DESCRIPTION_RE.exec(line.key);
    if (!identity) return line;
    const tail = PAYEE_TAIL_RE.exec(line.value.trim());
    if (!tail) return line;
    const [, description, payee] = tail;
    const payeeKey = `${identity[1]}.payee`;
    if (!payeeKeys.has(payeeKey)) {
      payeeKeys.add(payeeKey);
      extra.push({ ...line, key: payeeKey, value: payee });
    }
    return { ...line, value: description };
  });
  return [...cleaned, ...extra];
};
